import { inject } from '@angular/core';
import { Router, type CanMatchFn, type UrlSegment } from '@angular/router';

import { AuthService } from './auth.service';

/**
 * Gate for every protected route.
 *
 * canMatch, never canActivate: canActivate downloads the lazy chunk and THEN
 * rejects, so a user without the route still pays for its JavaScript.
 * canMatch skips the route before resolution, which is simultaneously a
 * bundle win and a security-hygiene one.
 *
 * Async because it awaits `ensureSession()` - the one memoized cookie refresh
 * per page load that replaces the app initializer the architecture
 * deliberately does not have.
 */
export const authGuard: CanMatchFn = async (_route, segments) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (await auth.ensureSession()) return true;

  return router.createUrlTree(['/login'], {
    queryParams: { returnUrl: currentUrl(router, segments) },
  });
};

/**
 * The URL the user was actually reaching for, query string included, so
 * `?returnUrl=` round-trips a deep link rather than dumping them on the
 * dashboard. `extractedUrl` is the pre-redirect URL and is available during
 * canMatch; the segment join is the fallback for a programmatic match.
 */
function currentUrl(router: Router, segments: readonly UrlSegment[]): string {
  const navigation = router.getCurrentNavigation();
  if (navigation) return navigation.extractedUrl.toString();
  return '/' + segments.map((segment) => segment.path).join('/');
}
